import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { toast } from 'sonner';

import { useQueryBuilder } from '@/sections/insights/queryBuilder/state/useQueryBuilder';
import {
  flatten,
  unflatten,
} from '@/sections/insights/queryBuilder/utils/flatGraphConversion';
import { normalizeQueryFilters } from '@/sections/insights/queryBuilder/utils/normalizeInFilters';
import { validateQuery } from '@/sections/insights/queryBuilder/utils/validateQuery';
import type { GraphQuery } from '@/types';

import { EditableQueryJSON, ValidationError } from './EditableQueryJSON';
import {
  validateGraphQuerySchema,
  validateJSONSyntax,
} from './utils/jsonValidation';

/**
 * QueryWriter - Pure JSON editor component
 *
 * Displays formatted JSON representation of the current query with
 * editable textarea and syntax highlighting.
 *
 * Features:
 * - Editable textarea with syntax highlighting
 * - Real-time JSON validation
 * - Execute/Save handlers for internal logic (triggered by parent ActionsToolbar)
 * - Rate limiting (1-2s cooldown between executions)
 * - Character limit (50KB max for security)
 * - Error handling with toast notifications
 *
 * Behavior:
 * - When queryState is null: Displays empty template
 * - When queryState exists: Displays unflattened query JSON
 * - Execute: Parses JSON → flattens → validates → dispatches RESET_GRAPH
 * - Save: Parses JSON → flattens → validates → saves to settings
 *
 * Layout responsibility: Parent component (QuerySplitWriter) handles split-pane layout
 * Actions responsibility: Parent component (ActionsToolbar) provides Execute/Save buttons
 *
 * Triggered by:
 * - QuerySplitWriter as leftContent in split layout
 */

/**
 * Imperative handle exposed to parent components
 * Allows parent to trigger execute action programmatically and get parsed query
 */
export interface QueryWriterHandle {
  parseAndExecute: () => GraphQuery | null;
}

export const QueryWriter = forwardRef<QueryWriterHandle>((_, ref) => {
  const { queryState, dispatch } = useQueryBuilder();

  // Rate limiting state (1-2s cooldown between executions)
  const [isExecuting, setIsExecuting] = useState(false);

  // Validation errors for JSON editor
  const [errors, setErrors] = useState<ValidationError[]>([]);

  // Force EditableQueryJSON to reset after successful Execute
  // Incremented when query executes successfully to trigger re-render with formatted JSON
  const [editorKey, setEditorKey] = useState(0);

  // Generate JSON to display from queryState
  // Include metadata fields (limit, orderBy, descending, shortest) from queryState.metadata
  const queryJson = queryState
    ? {
        ...unflatten(queryState.flatGraph),
        ...(queryState.metadata.limit !== undefined && {
          limit: queryState.metadata.limit,
        }),
        ...(queryState.metadata.orderBy && {
          orderBy: queryState.metadata.orderBy,
        }),
        ...(queryState.metadata.descending !== undefined && {
          descending: queryState.metadata.descending,
        }),
        ...(queryState.metadata.shortest !== undefined && {
          shortest: queryState.metadata.shortest,
        }),
      }
    : { node: { labels: [], filters: [] }, limit: 100 };

  const jsonString = JSON.stringify(queryJson, null, 2);

  // Ref to capture latest JSON from EditableQueryJSON
  const editorValueRef = useRef<string>(jsonString);

  /**
   * Execute handler: Parse JSON → Flatten → Validate → Dispatch
   * Implements complete validation pipeline with error handling
   * Called by EditableQueryJSON component after syntax/type validation passes
   */
  const handleExecute = (_validJson: string) => {
    // Rate limiting check
    if (isExecuting) {
      return;
    }

    setIsExecuting(true);
    // 1.5 second cooldown (middle of 1-2s range)
    setTimeout(() => setIsExecuting(false), 1500);

    // Phase 1: JSON syntax validation
    const syntaxErrors = validateJSONSyntax(editorValueRef.current);
    if (syntaxErrors.length > 0) {
      setErrors(syntaxErrors);
      toast.error(syntaxErrors[0].message);
      return; // Stop - don't proceed to Phase 2
    }

    try {
      // Parse JSON (syntax is valid at this point)
      const parsedQuery = JSON.parse(editorValueRef.current) as GraphQuery;

      // Phase 2: Schema validation
      const schemaErrors = validateGraphQuerySchema(
        editorValueRef.current,
        parsedQuery
      );
      if (schemaErrors.length > 0) {
        setErrors(schemaErrors);
        toast.error(schemaErrors[0].message);
        return; // Stop - don't execute query
      }

      // Clear errors on success
      setErrors([]);

      // Continue with existing validation and execution
      // Step 2: Flatten to FlatGraph
      const flatGraph = flatten(parsedQuery);

      // Step 3: Validate
      const validationResult = validateQuery(flatGraph);
      if (!validationResult.isValid) {
        toast.error(validationResult.errors[0]);
        return;
      }

      // Step 4: Dispatch RESET_GRAPH action
      dispatch({ type: 'RESET_GRAPH', flatGraph });

      // Step 5: Update metadata with limit/orderBy/descending/shortest from parsed JSON
      dispatch({
        type: 'UPDATE_METADATA',
        metadata: {
          limit: parsedQuery.limit,
          orderBy: parsedQuery.orderBy,
          descending: parsedQuery.descending,
          shortest: parsedQuery.shortest,
        },
      });

      // Step 6: Force editor to reset with formatted JSON
      setEditorKey(prev => prev + 1);
    } catch {
      // Should not happen as EditableQueryJSON validates, but handle anyway
      toast.error('Error processing query');
    }
  };

  /**
   * Parse, validate, and execute query - returns parsed query for parent to snapshot
   * Used by parent component (QuerySplitWriter) to get parsed query synchronously
   */
  const parseAndExecute = (): GraphQuery | null => {
    // Rate limiting check
    if (isExecuting) {
      return null;
    }

    setIsExecuting(true);
    setTimeout(() => setIsExecuting(false), 1500);

    // Phase 1: JSON syntax validation
    const syntaxErrors = validateJSONSyntax(editorValueRef.current);
    if (syntaxErrors.length > 0) {
      setErrors(syntaxErrors);
      toast.error(syntaxErrors[0].message);
      return null; // Stop - don't proceed to Phase 2
    }

    try {
      // Parse JSON (syntax is valid at this point)
      const parsedQuery = JSON.parse(editorValueRef.current) as GraphQuery;

      // Phase 2: Schema validation
      const schemaErrors = validateGraphQuerySchema(
        editorValueRef.current,
        parsedQuery
      );
      if (schemaErrors.length > 0) {
        setErrors(schemaErrors);
        toast.error(schemaErrors[0].message);
        return null; // Stop - don't execute query
      }

      // Clear errors on success
      setErrors([]);

      // Normalize forward IN filter values once (ENG-3855). The raw-JSON writer
      // never builds a FlatGraph for the value it ships, so it bypasses the
      // unflatten chokepoint where normalizeFilterValues runs. Normalizing here
      // keeps writer state (dispatches below) AND the returned execute/save
      // payload consistent.
      const normalizedQuery = normalizeQueryFilters(parsedQuery);

      // Continue with existing validation and execution
      // Step 2: Flatten to FlatGraph
      const flatGraph = flatten(normalizedQuery);

      // Step 3: Validate
      const validationResult = validateQuery(flatGraph);
      if (!validationResult.isValid) {
        toast.error(validationResult.errors[0]);
        return null;
      }

      // Step 4: Dispatch RESET_GRAPH action
      dispatch({ type: 'RESET_GRAPH', flatGraph });

      // Step 5: Update metadata with limit/orderBy/descending/shortest from parsed JSON
      dispatch({
        type: 'UPDATE_METADATA',
        metadata: {
          limit: normalizedQuery.limit,
          orderBy: normalizedQuery.orderBy,
          descending: normalizedQuery.descending,
          shortest: normalizedQuery.shortest,
        },
      });

      // Step 6: Force editor to reset with formatted JSON
      setEditorKey(prev => prev + 1);

      // Step 7: Return normalized query for parent to build snapshot
      return normalizedQuery;
    } catch {
      toast.error('Error processing query');
      return null;
    }
  };

  // Expose parseAndExecute method to parent component
  useImperativeHandle(ref, () => ({
    parseAndExecute,
  }));

  return (
    <EditableQueryJSON
      key={editorKey}
      value={jsonString}
      onChange={() => {}} // Not needed - EditableQueryJSON manages its own internal state
      onExecute={handleExecute}
      editable={true}
      valueRef={editorValueRef}
      errors={errors}
    />
  );
});

QueryWriter.displayName = 'QueryWriter';
